/**
 * Current Member Card
 * Displays the currently configured on-call member
 */

import React from 'react';
import {
  Stack,
  Text,
  MessageBarType,
  Shimmer,
  ShimmerElementType,
  useTheme
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';

interface CurrentMemberCardProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const CurrentMemberCard: React.FC<CurrentMemberCardProps> = () => {
  const { currentMember, loading } = useOnCallManager();
  const theme = useTheme();

  return (
    <Stack
      tokens={{ childrenGap: 12, padding: 20 }}
      styles={{
        root: {
          height: '100%',
          backgroundColor: theme.semanticColors.bodyBackground,
          color: theme.semanticColors.bodyText,
          borderRadius: 4,
          boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,0.132), 0 0.3px 0.9px 0 rgba(0,0,0,0.108)',
          selectors: {
            '.ms-Text': { color: theme.semanticColors.bodyText },
            '.ms-Label': { color: theme.semanticColors.bodyText }
          }
        }
      }}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: theme.semanticColors.bodyText } }}>
          Current On-Call Member
        </Text>

        {loading ? (
          <Stack tokens={{ childrenGap: 8 }}>
            <Shimmer
              shimmerElements={[
                { type: ShimmerElementType.line, height: 24, width: '60%' }
              ]}
            />
            <Shimmer
              shimmerElements={[
                { type: ShimmerElementType.line, height: 20, width: '80%' }
              ]}
            />
          </Stack>
        ) : currentMember ? (
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="xxLarge" styles={{ root: { fontWeight: 600, color: theme.palette.themePrimary } }}>
              {currentMember.fullName}
            </Text>
            <Text variant="medium" styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
              {currentMember.email}
            </Text>
            <Text variant="small" styles={{ root: { color: theme.semanticColors.disabledText, marginTop: 4 } }}>
              ID: {currentMember.memberId}
            </Text>
          </Stack>
        ) : (
          <Stack horizontalAlign="center" tokens={{ childrenGap: 8, padding: 20 }}>
            <Text variant="large" styles={{ root: { color: theme.semanticColors.disabledText } }}>
              No on-call member currently configured
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default CurrentMemberCard;
